import { SxProps, Theme } from "@mui/material";
/**
 * In some cases the Rich Text contains Explainer Macro from Umbraco with the following markup.
 * This works without javascript in the browser, just markup and css.
 * The following markup is rendered from Umbraco:
        <button popovertarget="@($"{explainerId}-popup")" id="@explainerId" class="explainer-trigger">
            <span class="explainer-term">@termInContentInContent</span>
            <svg class="explainer-trigger-icon" xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="currentColor" aria-hidden="true">
                <path d="M440-280h80v-240h-80v240Zm40-320q17 0 28.5-11.5T520-640q0-17-11.5-28.5T480-680q-17 0-28.5 11.5T440-640q0 17 11.5 28.5T480-600Zm0 520q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Zm0-80q134 0 227-93t93-227q0-134-93-227t-227-93q-134 0-227 93t-93 227q0 134 93 227t227 93Zm0-320Z" />
            </svg>
        </button>
        <span popover id="@($"{explainerId}-popup")" class="explainer-popup">
            <button class="explainer-popup-close" popovertarget="@($"{explainerId}-popup")">x</button>
            @selectedContent.Definition
        </span>
 */
export const sxExplainer: SxProps<Theme> = {
  ".explainer-trigger": {
    fontWeight: "inherit",
    cursor: "pointer",
    border: "unset",
    backgroundColor: "primary.lighter",
    p: 0,
    ":hover": {
      backgroundColor: "primary.light",
    },
    ".explainer-trigger-icon": {
      width: "1em",
      height: "1em",
      ml: "-0.1em",
      mb: "-0.1em",
    },
  },
  ".explainer-popup": {
    margin: 0,
    inset: "auto",
    left: "calc(50vw - 10em)",
    // Browser that fails the supportsAnchorPositioning test gets handled by applyExplainerEnhancements()
    top: "calc(anchor(bottom) + 0.8rem)",
    justifySelf: "anchor-center",
    maxWidth: "min(90vw, 20em)",
    padding: 2,
    border: "0.1rem solid",
    borderColor: "tertiary.light",
    borderRadius: 2,
    backgroundColor: "background.paper",
    textAlign: "left",
    fontSize: { xs: "1.3rem", md: "1.6rem" },
    fontWeight: "normal",
  },
  ".explainer-popup-close": {
    position: "absolute",
    top: "0.4rem",
    right: "0.4rem",
    backgroundColor: "transparent",
    border: "unset",
    cursor: "pointer",
    opacity: 0.5,
    [":hover"]: {
      opacity: 1,
    },
  },
};

// anchor and anchor-center and justify self requires https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_anchor_positioning
export const supportsAnchorPositioning =
  typeof CSS !== "undefined" &&
  !!CSS.supports?.("top: calc(anchor(bottom) + 0.8rem)") &&
  !!CSS.supports?.("justify-self: anchor-center");

function positionPair(trigger: HTMLElement, pop: HTMLElement) {
  const r = trigger.getBoundingClientRect();
  const scrollX = window.scrollX || document.documentElement.scrollLeft;
  const scrollY = window.scrollY || document.documentElement.scrollTop;
  const top = scrollY + r.bottom + 8;
  const left = scrollX + r.left + r.width / 2 - pop.offsetWidth / 2;
  Object.assign(pop.style, {
    position: "absolute",
    top: `${top}px`,
    left: `${Math.max(8, left)}px`,
  });
}

function setupFallbackFor(trigger: HTMLElement) {
  if (trigger.dataset.explainerBound === "1") return;
  const targetId = trigger.getAttribute("popovertarget");
  if (!targetId) return;
  const pop = document.getElementById(targetId);
  if (!pop) return;

  trigger.dataset.explainerBound = "1";

  const reposition = () => positionPair(trigger, pop);

  // Reposition when it is shown
  pop.addEventListener("toggle", () => {
    if (pop.matches(":popover-open")) {
      reposition();
    }
  });

  // Also observe size changes
  const ro = new ResizeObserver(reposition);
  ro.observe(trigger);
  ro.observe(pop);

  // Global listeners (debounced light)
  const onScrollOrResize = () => {
    if (pop.matches(":popover-open")) reposition();
  };
  window.addEventListener("scroll", onScrollOrResize, { passive: true });
  window.addEventListener("resize", onScrollOrResize);

  // Store cleanup if needed later
  (pop as any)._explainerCleanup = () => {
    ro.disconnect();
    window.removeEventListener("scroll", onScrollOrResize);
    window.removeEventListener("resize", onScrollOrResize);
  };
}

export function applyExplainerEnhancements(root: ParentNode = document) {
  if (supportsAnchorPositioning) return; // Native anchor CSS works
  const selector = "button[popovertarget].explainer-trigger";

  // Initial pass for already present nodes
  root.querySelectorAll<HTMLElement>(selector).forEach(setupFallbackFor);

  // Avoid multiple observers on same root
  if ((root as any)._explainerObserver) return;

  const processNode = (node: Node) => {
    if (!(node instanceof HTMLElement)) return;
    if (node.matches(selector)) setupFallbackFor(node);
    node.querySelectorAll?.(selector).forEach((el) => setupFallbackFor(el as HTMLElement));
  };

  const mo = new MutationObserver((muts) => {
    for (const m of muts) {
      if (m.type === "childList" && m.addedNodes.length) {
        m.addedNodes.forEach(processNode);
      }
    }
  });

  mo.observe(root, { childList: true, subtree: true });
  (root as any)._explainerObserver = mo;
}
